import { inject, Injectable } from '@angular/core';
import { nanoid } from 'nanoid';
import { NotificationCountdownOptions, NotificationOptions, NotificationService } from '../notification.service';
import { NotificationContrast, NotificationItem, NotificationType } from '../notification-item';
import { OverlayStackService } from '@shared/ui/kit/overlay-stack/overlay-stack.service';
import {
  DEFAULT_PLACEMENT,
  OverlayStackItem,
  OverlayStackItemKind
} from '@shared/ui/kit/overlay-stack/overlay-stack-item';

@Injectable()
export class BrowserNotificationService extends NotificationService {
  private readonly overlayStack: OverlayStackService = inject(OverlayStackService);

  show(options: NotificationOptions): string {
    return this.showNotification(this.createNotification(nanoid(), options));
  }

  showCountdown(options: NotificationCountdownOptions): string {
    return this.showNotification(this.createNotification(nanoid(), { ...options, type: NotificationType.countdown }));
  }

  update(notificationId: string, options: NotificationOptions): void {
    const notification = this.createNotification(notificationId, options);

    this.overlayStack.update(notificationId, this.toStackItem(notification));
  }

  dismiss(notificationId: string): void {
    this.overlayStack.remove(notificationId);
  }

  private showNotification(notification: NotificationItem): string {
    this.overlayStack.show(this.toStackItem(notification));

    return notification.id;
  }

  private toStackItem(notification: NotificationItem): OverlayStackItem {
    return {
      id: notification.id,
      kind: OverlayStackItemKind.notification,
      placement: DEFAULT_PLACEMENT.notification,
      durationMs: notification.durationMs,
      notification
    };
  }

  private createNotification(notificationId: string, options: NotificationOptions): NotificationItem {
    return {
      id: notificationId,
      type: options.type ?? NotificationType.neutral,
      contrast: options.contrast ?? NotificationContrast.default,
      title: options.title,
      message: options.message,
      message$: options.message$,
      isExpiring$: options.isExpiring$,
      durationMs: options.durationMs,
      link: options.link
    };
  }
}
